import { useCallback } from "react";
import { useFeatureRequestContext } from "../contexts/FeatureRequestContext";
import { featureRequestsApi } from "../api/featureRequests";
import {
  FeatureRequestSubmission,
  FeatureRequest,
} from "../types/featureRequest";

/**
 * Custom hook for feature request operations.
 *
 * Provides functions to load, submit, and vote on feature requests.
 * Integrates with FeatureRequestContext for centralized state management.
 * Handles API calls, loading states, and error management.
 *
 * @returns {Object} - Feature request state and actions:
 *   - features: array of feature requests
 *   - userVotes: set of feature IDs the user has voted for
 *   - isLoading: boolean indicating if data is being loaded
 *   - isSubmitting: boolean indicating if a submission is in progress
 *   - error: error message if any operation fails
 *   - loadFeatures: function to fetch all feature requests
 *   - submitFeature: function to submit a new feature request
 *   - voteForFeature: function to vote for a feature
 *   - removeVote: function to remove a vote for a feature
 *   - hasUserVoted: function to check if user has voted for a feature
 *   - addUserVote: function to add a vote to the user's local vote set
 *   - clearError: function to clear error state
 */
export const useFeatureRequest = () => {
  const {
    features,
    setFeatures,
    userVotes,
    setUserVotes,
    isLoading,
    setIsLoading,
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
  } = useFeatureRequestContext();

  /**
   * Loads all feature requests and user voting status
   * @param userId - The current user's ID
   * @returns Promise that resolves when features and user votes are loaded
   */
  const loadFeatures = useCallback(
    async (userId: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const [featuresResponse, userVotesData] = await Promise.all([
          featureRequestsApi.getFeatureRequests(),
          featureRequestsApi.getUserVotes(userId),
        ]);

        // Convert object to array and sort by votes in descending order
        const featuresArray = Object.values(featuresResponse).sort(
          (a, b) => b.votes - a.votes
        );

        setFeatures(featuresArray);
        setUserVotes(new Set(userVotesData));
      } catch (error) {
        console.error("Error loading features:", error);
        setError("Failed to load feature requests. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [setFeatures, setUserVotes, setIsLoading, setError]
  );

  /**
   * Submits a new feature request
   * @param submission - The feature request data containing title and description
   * @param userId - The current user's ID
   * @returns Promise that resolves to a response object with success status and optional error
   */
  const submitFeature = useCallback(
    async (submission: FeatureRequestSubmission, userId: string) => {
      setIsSubmitting(true);
      setError(null); // Clear any previous errors

      try {
        const response = await featureRequestsApi.submitFeatureRequest(
          submission,
          userId
        );

        if (response.error) {
          setError(response.error);
          return { success: false, error: response.error };
        }

        // Fetch and return updated features
        const featuresResponse = await featureRequestsApi.getFeatureRequests();
        const featuresArray = Object.values(featuresResponse).sort(
          (a, b) => b.votes - a.votes
        );
        setFeatures(featuresArray);
        return { ...response, features: featuresArray };
      } catch (error: any) {
        setError(error.message);
        return { success: false, error: error.message };
      } finally {
        setIsSubmitting(false);
      }
    },
    [setFeatures, setIsSubmitting, setError]
  );

  /**
   * Votes for a feature request
   * @param userId - The ID of the user voting for the feature
   * @param featureId - The ID of the feature to vote for
   * @returns Promise that resolves to a response object with success status and optional error
   */
  const voteForFeature = useCallback(
    async (userId: string, featureId: string) => {
      try {
        return await featureRequestsApi.voteForFeature(userId, featureId);
      } catch (error: any) {
        console.error("Failed to vote for feature:", error);
        return { success: false, error: error.message };
      }
    },
    []
  );

  /**
   * Removes a vote for a feature request
   * @param userId - The ID of the user removing their vote
   * @param featureId - The ID of the feature to remove the vote from
   * @returns Promise that resolves to a response object with success status, optional error, and updated features
   */
  const removeVote = useCallback(
    async (userId: string, featureId: string) => {
      try {
        const response = await featureRequestsApi.unvoteFeature(
          userId,
          featureId
        );
        // Update user votes locally
        const newVotes = new Set(userVotes);
        newVotes.delete(featureId);
        setUserVotes(newVotes);
        // Fetch and return updated features
        const featuresResponse = await featureRequestsApi.getFeatureRequests();
        const featuresArray = Object.values(featuresResponse).sort(
          (a, b) => b.votes - a.votes
        );
        setFeatures(featuresArray);
        return { ...response, features: featuresArray };
      } catch (error: any) {
        console.error("Failed to remove vote:", error);
        return { success: false, error: error.message };
      }
    },
    [userVotes, setFeatures, setUserVotes]
  );

  /**
   * Adds a vote for a feature to the user's local vote set
   * Used for optimistic UI updates when voting for a feature
   * @param featureId - The ID of the feature to add to user's votes
   */
  const addUserVote = useCallback(
    (featureId: string) => {
      setUserVotes(new Set([...userVotes, featureId]));
    },
    [userVotes, setUserVotes]
  );

  /**
   * Checks if the current user has voted for a specific feature
   * @param featureId - The ID of the feature to check
   * @returns boolean indicating if user has voted for the feature
   */
  const hasUserVoted = useCallback(
    (featureId: string): boolean => {
      return userVotes.has(featureId);
    },
    [userVotes]
  );

  /**
   * Clears the current error state
   * Used to reset error messages after they've been displayed or handled
   */
  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return {
    features,
    userVotes,
    isLoading,
    isSubmitting,
    error,
    loadFeatures,
    submitFeature,
    voteForFeature,
    removeVote,
    hasUserVoted,
    addUserVote,
    clearError,
  };
};
